'use client';
import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { Menu } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface AppLayoutProps {
  children: React.ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (!loading && !user) {
      router.push('/sign-up-login-screen');
    }
  }, [user, loading, router]);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full z-30 transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        <Sidebar
          collapsed={collapsed}
          onToggle={() => setCollapsed(p => !p)}
          onMobileClose={() => setMobileOpen(false)}
        />
      </div>

      {/* Main content — offset by sidebar width on desktop */}
      <main
        className="flex-1 overflow-x-hidden min-w-0 transition-all duration-300"
        style={{ paddingLeft: 0 }}
      >
        {/* Mobile topbar */}
        <div
          className="lg:hidden flex items-center gap-3 px-4 h-14 sticky top-0 z-10"
          style={{ background: 'linear-gradient(90deg, #003399 0%, #002580 100%)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}
        >
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>
          <span className="text-white font-700 text-base">BetProtect</span>
        </div>

        {/* Desktop: push content right of sidebar */}
        <div
          className="hidden lg:block"
          style={{ marginLeft: collapsed ? '64px' : '240px', transition: 'margin-left 0.3s' }}
        >
          <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-8">
            {children}
          </div>
        </div>

        {/* Mobile: full width */}
        <div className="lg:hidden">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-5">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}